import React, { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Collapse, IconButton, Typography } from "@mui/material";
import ChevronIcon from "@icons/myicons/arrow-down.svg?react";
import {
  toCurrency,
  toCurrencyWholeNumber,
  toCurrencyWithoutLargeCents,
  toPercent,
} from "@helpers";
import { InvestmentsChartDataStatistic } from "@api/v1/hooks/performance/useInvestmentsChartData";
import {
  GainLossPercents,
  GainLossTypography,
  MandatorTypography,
} from "@rentablo/components";
import { SmallCard } from "../SmallCard";

const DETAILS_ID = "investments-summary-details";

export interface InvestmentsTableSummaryMobileProps {
  statistic: InvestmentsChartDataStatistic;
  currency?: string;
  // The market value alone, e.g. above the bookings, in the same place.
  valueOnly?: boolean;
}

const Detail = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Box display="flex" flexDirection="column" gap={0.5} minWidth={0}>
    <Typography variant="caption" color="textSecondary" noWrap>
      {label}
    </Typography>
    {children}
  </Box>
);

// Phones show what moves during the day (market value and today's change);
// the figures that rarely change are one tap away.
export const InvestmentsTableSummaryMobile = ({
  statistic,
  currency = "EUR",
  valueOnly = false,
}: InvestmentsTableSummaryMobileProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleLabel = t(
    isExpanded
      ? "investmentsTable.summary.hideDetails"
      : "investmentsTable.summary.showDetails"
  );
  const totalReturn = statistic.valuationGainPercent ?? 0;

  return (
    <Box width="100%" mb={2} data-testid="investments-summary-mobile">
      {/* One height with or without today's change, so switching between
          the investments and bookings tabs doesn't shift the list. */}
      <Box
        display="flex"
        alignItems="flex-end"
        justifyContent="space-between"
        gap={3}
        minHeight={(theme) => theme.spacing(13)}
      >
        <Box minWidth={0}>
          <Typography variant="caption" color="textSecondary" component="div">
            {t("investmentsTable.summary.marketValue")}
          </Typography>
          <MandatorTypography
            variant="h6"
            kpmg-germany={{ variant: "subtitle2" }}
            noWrap
          >
            {toCurrencyWholeNumber(statistic.portfolioValue, currency)}
          </MandatorTypography>
        </Box>
        {!valueOnly && (
          <Box display="flex" alignItems="flex-end" gap={0.5}>
            <Box display="flex" flexDirection="column" alignItems="flex-end">
              <Typography
                variant="caption"
                color="textSecondary"
                component="div"
              >
                {t("investmentsTable.summary.today")}
              </Typography>
              <Box display="flex" alignItems="center" gap={1.5}>
                <GainLossTypography
                  variant="body4"
                  value={statistic.changeTodayAbs}
                >
                  {toCurrency(statistic.changeTodayAbs, currency)}
                </GainLossTypography>
                <GainLossPercents value={statistic.changeToday} />
              </Box>
            </Box>
            <IconButton
              aria-label={toggleLabel}
              aria-expanded={isExpanded}
              aria-controls={DETAILS_ID}
              onClick={() => setIsExpanded((expanded) => !expanded)}
              sx={(theme) => ({
                mr: -2.5,
                mb: -1.5,
                color: "icon.secondary",
                // The rotating chevron is the feedback; no hover or pressed
                // background, which sticks after a tap on phones. Keyboard
                // focus still gets a ring.
                "&:hover, &:active": { backgroundColor: "transparent" },
                "&.Mui-focusVisible": {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: -2,
                },
              })}
            >
              <ChevronIcon
                width="20px"
                height="20px"
                style={{
                  transform: isExpanded ? "rotate(180deg)" : undefined,
                  transition: "transform 120ms",
                }}
              />
            </IconButton>
          </Box>
        )}
      </Box>
      {!valueOnly && (
        <Collapse in={isExpanded} unmountOnExit>
          <SmallCard
            id={DETAILS_ID}
            // The asset class pills' padding.
            sx={{
              mt: 4,
              px: 3,
              pt: 2.5,
              pb: 3,
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 3,
            }}
          >
            <Detail label={t("investmentsTable.summary.purchaseValue")}>
              <Typography variant="body4" noWrap>
                {toCurrencyWithoutLargeCents(
                  statistic.investedCapital,
                  currency
                )}
              </Typography>
            </Detail>
            <Detail label={t("investmentsTable.summary.priceGains")}>
              <GainLossTypography
                variant="body4"
                value={statistic.valuationGain}
              >
                {toCurrencyWithoutLargeCents(statistic.valuationGain, currency)}
              </GainLossTypography>
            </Detail>
            <Detail label={t("investmentsTable.summary.totalReturn")}>
              <GainLossTypography variant="body4" value={totalReturn}>
                {toPercent(totalReturn, "exceptZero")}
              </GainLossTypography>
            </Detail>
          </SmallCard>
        </Collapse>
      )}
    </Box>
  );
};
